/**
 * Events API — Supabase PostgREST queries.
 */

import supabase from "../config/supabase";

const EVENT_SELECT = `
  *,
  locations:event_locations(location_id, locations(*)),
  user:users(id, first_name, last_name, profile_image_url, role, is_placeholder)
`;

const invokeAiModeration = async (body) => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) return;
  supabase.functions.invoke("ai-moderation", {
    body,
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
};

const eventsApi = {
  async list(params = {}) {
    let serviceIds = null;

    if (params.location) {
      const names = params.location.split("|");
      const { data: locRows } = await supabase.from("locations").select("id").in("name", names);
      const locIds = (locRows || []).map((l) => l.id);
      if (!locIds.length) return { success: true, data: { items: [], total: 0 } };
      const { data: rows } = await supabase.from("event_locations").select("event_id").in("location_id", locIds);
      serviceIds = [...new Set((rows || []).map((r) => r.event_id))];
      if (!serviceIds.length) return { success: true, data: { items: [], total: 0 } };
    }

    let query = supabase.from("events").select(EVENT_SELECT);

    // Owners see their own events at any status; everyone else sees only active.
    if (params.user_id) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id === params.user_id) {
        query = query.eq("user_id", params.user_id);
      } else {
        query = query.eq("status", "active").eq("user_id", params.user_id);
      }
    } else {
      query = query.eq("status", "active");
    }

    if (serviceIds) query = query.in("id", serviceIds);
    query = query.order("start_date", { ascending: true });
    if (params.limit) query = query.limit(params.limit);
    if (params.page && params.limit) {
      const offset = (params.page - 1) * params.limit;
      query = query.range(offset, offset + params.limit - 1);
    }

    const { data, error } = await query;
    if (error) return { success: false, error: error.message };
    const items = (data || []).map(flattenEvent);
    return { success: true, data: { items, total: serviceIds ? serviceIds.length : items.length } };
  },

  async get(eventId) {
    const { data, error } = await supabase
      .from("events")
      .select(EVENT_SELECT)
      .eq("id", eventId)
      .single();
    if (error) return { success: false, error: error.message };
    return { success: true, data: flattenEvent(data) };
  },

  async create(body) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { locations: locNames, ...eventData } = body;
    eventData.user_id = user.id;

    // Admins/moderators skip the queue — go straight to active (mirrors jobs/services).
    const { data: profile } = await supabase.from("users").select("role").eq("id", user.id).single();
    eventData.status = (profile?.role === "admin" || profile?.role === "moderator") ? "active" : "pending";

    const { data, error } = await supabase.from("events").insert(eventData).select().single();
    if (error) return { success: false, error: error.message };

    if (locNames?.length) {
      const { data: locs } = await supabase.from("locations").select("id").in("name", locNames);
      if (locs?.length) {
        await supabase.from("event_locations").insert(
          locs.map((l) => ({ event_id: data.id, location_id: l.id }))
        );
      }
    }

    return { success: true, data };
  },

  async update(eventId, body) {
    const { data, error } = await supabase.from("events").update(body).eq("id", eventId).select().single();
    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },

  async submit(eventId) {
    const { data: evt, error } = await supabase
      .from("events")
      .select("status, title, description")
      .eq("id", eventId)
      .single();
    if (error) return { success: false, error: error.message };
    if (evt?.status !== "pending") return { success: true };

    if (evt) {
      invokeAiModeration({ item_id: eventId, item_type: "event" });
    }

    return { success: true };
  },

  async delete(eventId) {
    const { error } = await supabase.from("events").delete().eq("id", eventId);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },
};

function flattenEvent(row) {
  if (!row) return row;
  return {
    ...row,
    locations: (row.locations || []).map((jt) => jt.locations).filter(Boolean),
  };
}

export default eventsApi;
